import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Check, Users, Gift, Link2, ShieldCheck, Clock, Loader2, Ticket } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralPage = () => {
  const { isAuthenticated, loginWithDiscord, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState(false);
  const [copied, setCopied] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/referral/invite-stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        if (data.invite_url) setInviteUrl(data.invite_url);
      }
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && token) fetchStats();
    else setLoading(false);
  }, [isAuthenticated, token, fetchStats]);

  const generateLink = async () => {
    setLinkLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/referral/invite-link`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok && data.invite_url) setInviteUrl(data.invite_url);
    } catch (e) { /* noop */ } finally { setLinkLoading(false); }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const count = stats?.invite_count ?? 0;
  const pct = stats?.discount_percent ?? 0;
  const next = stats?.next_tier;
  const tiers = stats?.tiers ?? [{ invites: 5, percent: 5 }, { invites: 10, percent: 10 }, { invites: 20, percent: 15 }];
  const maxTier = tiers[tiers.length - 1]?.invites || 20;
  const progress = Math.min(100, Math.round((count / maxTier) * 100));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="referral-loading">
        <Loader2 className="w-7 h-7 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-14 px-4 md:px-8" data-testid="referral-page">
      <div className="max-w-3xl mx-auto">

        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-semibold mb-3">
            <Gift className="w-3.5 h-3.5" /> Programme de parrainage
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
            Invite tes amis, <span className="text-cyan-400">gagne des réductions</span>
          </h1>
          <p className="text-gray-400 text-sm md:text-base max-w-xl mx-auto">
            Partage ton lien. Chaque ami qui rejoint le Discord te rapporte 1 invitation.
            Plus tu invites, plus ta réduction sur ton optimisation grimpe. 🚀
          </p>
        </div>

        {!isAuthenticated ? (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-8 text-center" data-testid="referral-login-card">
            <Users className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
            <p className="text-white font-semibold mb-1">Connecte-toi pour obtenir ton lien</p>
            <p className="text-gray-400 text-sm mb-5">Récupère ton lien d'invitation unique et suis tes récompenses.</p>
            <Button onClick={loginWithDiscord} className="bg-[#5865F2] hover:bg-[#4752C4] text-white" data-testid="referral-login-btn">
              Se connecter avec Discord
            </Button>
          </div>
        ) : (
          <>
            {/* Ton lien */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 md:p-6 mb-5" data-testid="referral-link-card">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-cyan-400" />
                <h2 className="text-white font-bold">Ton lien d'invitation</h2>
              </div>
              {inviteUrl ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    readOnly
                    value={inviteUrl}
                    data-testid="referral-invite-input"
                    className="flex-1 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2.5 text-cyan-300 text-sm font-mono"
                  />
                  <button
                    onClick={() => copy(inviteUrl, 'link')}
                    data-testid="referral-copy-link-btn"
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-cyan-500 text-white text-sm font-semibold rounded-lg hover:bg-cyan-400 transition-colors"
                  >
                    {copied === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied === 'link' ? 'Copié !' : 'Copier'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={generateLink}
                  disabled={linkLoading}
                  data-testid="referral-generate-link-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-cyan-500 text-white text-sm font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50"
                >
                  {linkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                  Générer mon lien d'invitation
                </button>
              )}
            </div>

            {/* Progression */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 md:p-6 mb-5" data-testid="referral-progress-card">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="text-4xl font-bold text-white tabular-nums" data-testid="referral-invite-count">{count}</div>
                  <div className="text-gray-400 text-xs">invitation{count > 1 ? 's' : ''} validée{count > 1 ? 's' : ''}</div>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-cyan-400 tabular-nums" data-testid="referral-discount">-{pct}%</div>
                  <div className="text-gray-400 text-xs">réduction actuelle</div>
                </div>
              </div>

              <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden mb-2">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-300 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              {next ? (
                <p className="text-gray-400 text-xs">
                  Encore <span className="text-cyan-400 font-semibold">{next.needed} invitation{next.needed > 1 ? 's' : ''}</span> pour débloquer <span className="text-cyan-400 font-semibold">-{next.percent}%</span>
                </p>
              ) : (
                <p className="text-cyan-400 text-xs font-semibold">🎉 Palier maximum atteint — tu profites de la réduction max !</p>
              )}

              {stats?.pending_count > 0 && (
                <p className="text-amber-400/90 text-xs mt-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {stats.pending_count} invitation{stats.pending_count > 1 ? 's' : ''} en attente de validation (5 jours)
                </p>
              )}
            </div>

            {/* Code promo retiré : la réduction s'applique automatiquement sur le prix des optimisations (site) */}
            {pct > 0 && (
              <div className="flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-xl px-4 py-3 mb-5 text-sm text-cyan-200" data-testid="referral-auto-note">
                <Ticket className="w-4 h-4 flex-shrink-0 text-cyan-400" />
                <span>Ta réduction de <b className="text-white">-{pct}%</b> s'applique <b className="text-white">automatiquement</b> sur le prix quand tu commandes une optimisation sur le site (connecté avec Discord).</span>
              </div>
            )}

            {/* Paliers */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              {tiers.map((tr) => {
                const reached = count >= tr.invites;
                return (
                  <div
                    key={tr.invites}
                    data-testid={`referral-tier-${tr.invites}`}
                    className={`rounded-xl p-4 text-center border transition-all ${reached ? 'border-cyan-500 bg-cyan-500/10' : 'border-zinc-800 bg-zinc-900/40'}`}
                  >
                    <div className={`text-2xl font-bold ${reached ? 'text-cyan-400' : 'text-gray-500'}`}>-{tr.percent}%</div>
                    <div className="text-gray-400 text-xs mt-1">{tr.invites} invitations</div>
                    {reached && <Check className="w-4 h-4 text-cyan-400 mx-auto mt-1" />}
                  </div>
                );
              })}
            </div>

            {/* Invitations récentes */}
            {stats?.recent?.length > 0 && (
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 mb-5" data-testid="referral-recent-card">
                <h2 className="text-white font-bold mb-3 text-sm">Tes invitations récentes</h2>
                <div className="space-y-2">
                  {stats.recent.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300 flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-gray-500" /> {r.invitee_name}
                      </span>
                      {r.status === 'validated'
                        ? <span className="text-cyan-400 text-xs font-semibold flex items-center gap-1"><Check className="w-3 h-3" /> Validée</span>
                        : <span className="text-amber-400 text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> En attente</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Comment ça marche */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-5 md:p-6">
          <h2 className="text-white font-bold mb-4">Comment ça marche ?</h2>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs font-bold">1</span><span className="text-gray-300">Copie ton lien d'invitation unique et partage-le à tes amis.</span></li>
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs font-bold">2</span><span className="text-gray-300">Ton ami clique sur le lien et <b className="text-white">rejoint le serveur Discord</b> → +1 invitation.</span></li>
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs font-bold">3</span><span className="text-gray-300">Atteins un palier → ta réduction s'applique <b className="text-white">automatiquement sur le site</b> pour ton optimisation.</span></li>
          </ol>
          <div className="mt-4 pt-4 border-t border-zinc-800 flex items-start gap-2 text-xs text-gray-500">
            <ShieldCheck className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
            <span>Anti-triche : seuls les comptes Discord de plus de 30 jours comptent, et l'invitation n'est validée qu'après 5 jours de présence (annulée si la personne quitte le serveur).</span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ReferralPage;
